import React, { useEffect, useRef, useState } from "react";

import { AnalyzeAPI, Ai360UserAPI, SearchAPI } from "@ai360/core";
import { selectors as cdSelectors } from "~/customer-data";
import { getLoginGuid, getTheUserCompanyGuid, getTheUserGuid } from "~/login/selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { DialogTool } from "~/core/components/dialog-box/dialog-tool";
import { PrintIcon } from "~/core/icons";
import { logFirebaseEvent } from "~/utils/firebase";

import MarkdownRenderer from "./markdownrenderer";
import "./chat.css";

interface ChatMessage {
    content: string;
    role: "assistant" | "user";
}

interface HistoryMessage {
    content: string;
    role: string;
}

const WELCOME_MESSAGE: ChatMessage = {
    content: "Hello! I'm your AI assistant. Ask me anything about your Fields and agronomic data.",
    role: "assistant",
};

function extractRealContent(buffer: string): string | null {
    const newlineIdx = buffer.indexOf("\n");
    const firstLine = newlineIdx === -1 ? buffer : buffer.substring(0, newlineIdx);
    const colonCount = (firstLine.match(/:/g) ?? []).length;
    const hasThinkingPattern = colonCount >= 3;

    if (hasThinkingPattern) {
        const hashIdx = buffer.indexOf("#");
        if (hashIdx !== -1) {
            return buffer.substring(hashIdx);
        }
        const doubleNewlineIdx = buffer.indexOf("\n\n");
        if (doubleNewlineIdx !== -1) {
            const after = buffer.substring(doubleNewlineIdx + 2);
            return after.length > 0 ? after : null;
        }
        return null;
    }

    if (buffer.includes("\n")) {
        return buffer;
    }
    return null;
}

export function AiChat(): JSX.Element {
    const dispatch = useAppDispatch();
    const selectedFieldGuidSet = useAppSelector(cdSelectors.getSelectedFieldGuids);
    const loginGuid = useAppSelector(getLoginGuid);
    const userGuid = useAppSelector(getTheUserGuid);
    const companyGuid = useAppSelector(getTheUserCompanyGuid);

    const { data: creditData } = Ai360UserAPI.useCheckBedrockCreditQuery(
        { loginGuid, userGuid },
        { skip: !loginGuid || !userGuid, refetchOnMountOrArgChange: true }
    );

    const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
    const [inputText, setInputText] = useState<string>("");
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [modelType, setModelType] = useState<string>("haiku");

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const lastSentTextRef = useRef<string>("");
    const abortRef = useRef<AbortController | null>(null);
    const lastValidatedFieldKey = useRef<string | null>(null);
    const lastValidationResult = useRef<{ isValid: boolean; invalidCustomers: string[] } | null>(
        null
    );

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const buildHistory = (msgs: ChatMessage[]): HistoryMessage[] => {
        return msgs.map((m) => ({ content: m.content, role: m.role }));
    };

    const updateLastAssistant = (msgs: ChatMessage[], content: string): ChatMessage[] => {
        const next = [...msgs];
        next[next.length - 1] = { content, role: "assistant" };
        return next;
    };

    const checkCommunityData = async (
        fieldGuids: string[]
    ): Promise<{ isValid: boolean; invalidCustomers: string[] }> => {
        const key = [...fieldGuids].sort().join(",");
        if (key === lastValidatedFieldKey.current && lastValidationResult.current !== null) {
            return lastValidationResult.current;
        }
        const result = await AnalyzeAPI.checkCommunityData(fieldGuids);
        lastValidatedFieldKey.current = key;
        lastValidationResult.current = result;
        return result;
    };

    const sendMessage = async (text: string): Promise<void> => {
        if (!text.trim() || isStreaming) {
            return;
        }

        const fieldGuids = Array.from(selectedFieldGuidSet).filter((guid): guid is string =>
            Boolean(guid)
        );
        setIsValidating(true);
        try {
            const { isValid, invalidCustomers } = await checkCommunityData(fieldGuids);
            if (!isValid) {
                setErrorMessage(
                    `Some fields belong to customers with restricted community data: ${invalidCustomers.join(
                        ", "
                    )}.`
                );
                return;
            }
        } catch {
            setErrorMessage("Couldn't verify field access. Please try again.");
            return;
        } finally {
            setIsValidating(false);
        }

        setErrorMessage(null);
        lastSentTextRef.current = text;

        const history = buildHistory(messages);

        setMessages((prev) => [
            ...prev,
            { content: text, role: "user" },
            { content: "", role: "assistant" },
        ]);
        setInputText("");
        setIsStreaming(true);
        logFirebaseEvent("chat_message_sent", { userGuid: loginGuid });

        let streamBuffer = "";
        let contentStarted = false;

        try {
            const controller = new AbortController();
            abortRef.current = controller;

            // Resolve field details via the search API (like Quick Summary
            // does), not a local field cache that can lag behind selection.
            const fieldResults = await SearchAPI.getFields({ userGuid, fieldGuid: fieldGuids });
            const fields = fieldResults.map((f) => ({
                fieldGuid: f.id,
                fieldName: f.name,
                customerName: f.customerName,
                farmName: f.farmName,
            }));

            await AnalyzeAPI.sendChatMessage(
                fields,
                history,
                text,
                loginGuid,
                companyGuid,
                modelType,
                (chunk: string) => {
                    const lines = chunk.split(/\n|\}\{/).filter((l) => l.trim().length > 0);

                    for (const line of lines) {
                        let jsonStr = line.trim();
                        if (!jsonStr.startsWith("{")) {
                            jsonStr = "{" + jsonStr;
                        }
                        if (!jsonStr.endsWith("}")) {
                            jsonStr = jsonStr + "}";
                        }

                        try {
                            const parsed = JSON.parse(jsonStr);
                            if (
                                parsed.type === "content_block_delta" &&
                                parsed.delta?.type === "text_delta"
                            ) {
                                streamBuffer += parsed.delta.text;

                                if (!contentStarted) {
                                    const real = extractRealContent(streamBuffer);
                                    if (real !== null) {
                                        contentStarted = true;
                                        streamBuffer = real;
                                        setMessages((prev) => updateLastAssistant(prev, real));
                                    }
                                } else {
                                    setMessages((prev) => updateLastAssistant(prev, streamBuffer));
                                }
                            }
                        } catch {
                            // Partial JSON chunk — skip and wait for next read
                        }
                    }
                },
                controller.signal
            );

            // Flush any remaining buffer if preamble filtering held content back
            if (!contentStarted && streamBuffer.trim().length > 0) {
                setMessages((prev) => updateLastAssistant(prev, streamBuffer.trim()));
            }
        } catch (err: unknown) {
            if (err instanceof Error && err.name === "AbortError") {
                return;
            }
            setMessages((prev) => {
                const next = [...prev];
                if (next[next.length - 1]?.content === "") {
                    next.pop();
                }
                return next;
            });
            setErrorMessage("Something went wrong. Please try again.");
        } finally {
            setIsStreaming(false);
            abortRef.current = null;
            if (loginGuid && userGuid) {
                dispatch(
                    Ai360UserAPI.endpoints.checkBedrockCredit.initiate(
                        { loginGuid, userGuid },
                        { subscribe: false, forceRefetch: true }
                    )
                );
            }
        }
    };

    const retry = (): void => {
        const text = lastSentTextRef.current;
        if (!text) {
            return;
        }
        setErrorMessage(null);
        sendMessage(text);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage(inputText);
        }
    };

    const noFieldsSelected = selectedFieldGuidSet.size === 0;
    const canShare = messages.some((m) => m.role === "user");
    const creditIsAllowed = creditData?.isAllowed !== false;

    const getCreditColor = () => {
        if (!creditData) return "green";
        if (!creditData.isAllowed) return "red";
        const progress =
            creditData.dailyLimit > 0 ? creditData.currentUsage / creditData.dailyLimit : 0;
        if (progress >= 0.8) return "orange";
        if (progress >= 0.5) return "yellow";
        return "green";
    };

    const handleShare = async (): Promise<void> => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const markdown = messages
                .map((m) => (m.role === "user" ? `**You:** ${m.content}` : m.content))
                .join("\n\n---\n\n");

            const pdf = await AnalyzeAPI.exportChatPdf(markdown);
            const bytes = Uint8Array.from(atob(pdf), (c) => c.charCodeAt(0));
            const blob = new Blob([bytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);

            const date = new Date()
                .toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
                .replace(/\//g, "-");
            const randomNum = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

            const link = document.createElement("a");
            link.href = url;
            link.download = `AI_Chat_${date}_${randomNum}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            // Silently fail — could wire up an error toast here if needed
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="ai-chat-panel add-edit-panel">
            {noFieldsSelected && (
                <div className="ai-chat-no-fields">
                    Select one or more fields from the field list to get started.
                </div>
            )}
            {canShare && (
                <div className="ai-chat-share-row">
                    <DialogTool onClick={handleShare}>
                        {isExporting ? (
                            <span className="ai-chat-share-spinner" />
                        ) : (
                            <PrintIcon className="ai-chat-share-icon" />
                        )}
                    </DialogTool>
                </div>
            )}
            <div className="ai-chat-messages">
                {messages.map((msg, idx) => (
                    <div className={`ai-chat-bubble-row ${msg.role}`} key={idx}>
                        <div className={`ai-chat-bubble ${msg.role}`}>
                            {msg.role === "assistant" ? (
                                msg.content.length > 0 ? (
                                    <MarkdownRenderer markdown={msg.content} />
                                ) : isStreaming && idx === messages.length - 1 ? (
                                    <div className="ai-chat-typing">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                ) : null
                            ) : (
                                <span>{msg.content}</span>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {errorMessage !== null && (
                <div className="ai-chat-error">
                    <span className="ai-chat-error-text">{errorMessage}</span>
                    <button className="ai-chat-retry-btn" onClick={retry} type="button">
                        ↩ Retry
                    </button>
                </div>
            )}

            <div className="ai-chat-model-row">
                <select
                    className="ai-chat-model-select"
                    disabled={isStreaming}
                    onChange={(e) => setModelType(e.target.value)}
                    value={modelType}
                >
                    <option value="sonnet">Claude Sonnet — Highest</option>
                    <option value="haiku">Claude Haiku — High</option>
                    <option value="nova-pro">Nova Pro — Medium</option>
                    <option value="nova-lite">Nova Lite — Low</option>
                </select>
            </div>

            <div className="ai-chat-input-bar">
                <textarea
                    className="ai-chat-input"
                    disabled={isStreaming || isValidating || noFieldsSelected || !creditIsAllowed}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        noFieldsSelected
                            ? "Select fields to begin..."
                            : "Type a question about your Fields..."
                    }
                    rows={1}
                    value={inputText}
                />
                <button
                    className="ai-chat-send-btn"
                    disabled={
                        isStreaming ||
                        isValidating ||
                        inputText.trim().length === 0 ||
                        noFieldsSelected ||
                        !creditIsAllowed
                    }
                    onClick={() => sendMessage(inputText)}
                    type="button"
                >
                    {isValidating ? <span className="ai-chat-send-spinner" /> : "↑"}
                </button>
            </div>

            <p className="ai-chat-disclaimer">* AI Chat uses AI and can make mistakes.</p>
            {!creditIsAllowed && (
                <p className="ai-chat-credit-limit-notice">
                    Daily credit limit reached. You cannot send more messages today.
                </p>
            )}
            {creditData && (
                <div className="ai-chat-credit-footer">
                    <div className="ai-chat-credit-bar">
                        <div
                            className={`ai-chat-credit-fill ${getCreditColor()}`}
                            style={{
                                width: `${Math.min(
                                    (creditData.currentUsage / creditData.dailyLimit) * 100,
                                    100
                                )}%`,
                            }}
                        />
                    </div>
                    <span
                        className={`ai-chat-credit-label ${
                            getCreditColor() === "red"
                                ? "red"
                                : getCreditColor() === "orange"
                                ? "orange"
                                : ""
                        }`}
                    >
                        {creditData.currentUsage} / {creditData.dailyLimit} credits used today
                    </span>
                </div>
            )}
        </div>
    );
}
